import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import CrmUpgrade from "./CrmUpgrade";
import {
  crmFetch, STAGES, stageInfo, activityInfo,
  formatMoney, formatDateTime, dueLabel,
} from "./crmApi";
import "./Crm.css";

/**
 * CRM Overview: the studio's sales picture at a glance — pipeline counts,
 * this month's bookings, follow-ups coming due, and the latest activity.
 */
function CrmDashboard() {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planBlocked, setPlanBlocked] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await crmFetch("/api/crm/summary"));
    } catch (e) {
      if (e.status === 401) navigate("/login");
      if (e.status === 403 && e.data?.code === "upgrade_required") setPlanBlocked(true);
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const stages = summary?.stages || {};
  // Guard the arrays: an unexpected response shape must degrade to empty
  // states, not crash the page (the desktop ui-e2e walks /crm with a mock API).
  const followUps = summary?.upcomingFollowUps || [];
  const recentActivities = summary?.recentActivities || [];
  const openLeads = ["new", "contacted", "quote_sent"]
    .reduce((sum, k) => sum + (stages[k]?.count || 0), 0);
  const pipelineValue = ["new", "contacted", "quote_sent"]
    .reduce((sum, k) => sum + (stages[k]?.quotedTotal || 0), 0);
  const month = summary?.thisMonth;
  const conversion = month && month.leadsCreated > 0
    ? Math.round((month.booked / month.leadsCreated) * 100)
    : null;

  return (
    <div className="crm-container">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="crm-main">
        <header className="crm-header">
          <div className="crm-header-left">
            <button className="crm-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>☰</button>
            <div>
              <h2>📊 CRM Overview</h2>
              <p className="crm-header-sub">Your enquiries, bookings and follow-ups in one place</p>
            </div>
          </div>
          <button className="crm-primary-btn" onClick={() => navigate("/crm/leads?new=1")}>
            ＋ New Lead
          </button>
        </header>

        <div className="crm-content">
          {planBlocked ? (
            <CrmUpgrade />
          ) : loading ? (
            <div className="crm-loading">Loading your CRM…</div>
          ) : !summary ? (
            <div className="crm-empty">Couldn't load the CRM summary. Please try again.</div>
          ) : (
            <>
              <div className="crm-stats-row">
                <div className="crm-stat-card" style={{ borderTopColor: "#3b82f6" }}
                     onClick={() => navigate("/crm/leads")}>
                  <div className="crm-stat-value">{openLeads}</div>
                  <div className="crm-stat-label">🎯 Open leads</div>
                  {formatMoney(pipelineValue) && (
                    <div className="crm-stat-extra">{formatMoney(pipelineValue)} in pipeline</div>
                  )}
                </div>
                <div className="crm-stat-card" style={{ borderTopColor: "#10b981" }}
                     onClick={() => navigate("/crm/leads")}>
                  <div className="crm-stat-value">{month?.booked ?? 0}</div>
                  <div className="crm-stat-label">✅ Booked this month</div>
                  {formatMoney(month?.bookedValue) && (
                    <div className="crm-stat-extra">{formatMoney(month.bookedValue)} booked value</div>
                  )}
                </div>
                <div className="crm-stat-card" style={{ borderTopColor: "#8b5cf6" }}
                     onClick={() => navigate("/crm/leads")}>
                  <div className="crm-stat-value">{month?.leadsCreated ?? 0}</div>
                  <div className="crm-stat-label">🌱 New enquiries this month</div>
                  {conversion !== null && (
                    <div className="crm-stat-extra">{conversion}% became bookings</div>
                  )}
                </div>
                <div className="crm-stat-card" style={{ borderTopColor: "#dc2626" }}
                     onClick={() => navigate("/crm/followups")}>
                  <div className="crm-stat-value">
                    {(summary.tasks?.overdue ?? 0) + (summary.tasks?.today ?? 0)}
                  </div>
                  <div className="crm-stat-label">⏰ Follow-ups due</div>
                  {(summary.tasks?.overdue ?? 0) > 0 && (
                    <div className="crm-stat-extra" style={{ color: "#dc2626" }}>
                      {summary.tasks.overdue} overdue
                    </div>
                  )}
                </div>
              </div>

              <div className="crm-overview-grid">
                <div className="crm-card">
                  <h3 className="crm-section-title">📅 Follow-ups coming up</h3>
                  {followUps.length === 0 ? (
                    <div className="crm-empty" style={{ padding: "24px 10px" }}>
                      <span className="crm-empty-icon">🌤️</span>
                      No follow-ups due in the next 7 days.
                      <div className="crm-empty-hint">
                        Set a follow-up date on a lead so nothing slips.
                      </div>
                    </div>
                  ) : (
                    followUps.map((lead) => {
                      const due = dueLabel(lead.next_follow_up);
                      const st = stageInfo(lead.stage);
                      return (
                        <div key={lead.id} className="crm-list-row">
                          <div className="crm-row-main">
                            <div className="crm-row-title">{lead.client_name}</div>
                            <div className="crm-row-sub">
                              {st.icon} {st.label}
                              {lead.event_type ? ` · ${lead.event_type}` : ""}
                              {lead.client_phone ? ` · ${lead.client_phone}` : ""}
                            </div>
                          </div>
                          {due && <span className={`crm-due ${due.tone}`}>{due.text}</span>}
                        </div>
                      );
                    })
                  )}
                  <div style={{ textAlign: "right", marginTop: 8 }}>
                    <button className="crm-link-btn" onClick={() => navigate("/crm/leads")}>
                      Open the pipeline →
                    </button>
                  </div>
                </div>

                <div className="crm-card">
                  <h3 className="crm-section-title">🕘 Recent activity</h3>
                  {recentActivities.length === 0 ? (
                    <div className="crm-empty" style={{ padding: "24px 10px" }}>
                      <span className="crm-empty-icon">📝</span>
                      Nothing logged yet.
                      <div className="crm-empty-hint">
                        Calls, WhatsApp chats and meetings you log will show here.
                      </div>
                    </div>
                  ) : (
                    <ul className="crm-timeline">
                      {recentActivities.map((a) => {
                        const t = activityInfo(a.type);
                        return (
                          <li key={a.id}>
                            <span className="t-icon">{t.icon}</span>
                            <div className="t-body">
                              <div className="t-summary">
                                {a.client_name ? <strong>{a.client_name}: </strong> : null}
                                {a.summary}
                              </div>
                              <div className="t-when">{formatDateTime(a.activity_date)}</div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="crm-card" style={{ marginTop: 18 }}>
                <h3 className="crm-section-title">🧭 Pipeline snapshot</h3>
                <div className="crm-stage-row">
                  {STAGES.map((s) => (
                    <button
                      key={s.key}
                      className="crm-stage-chip"
                      style={{ borderColor: s.color, color: s.color }}
                      onClick={() => navigate(`/crm/leads?stage=${s.key}`)}
                    >
                      {s.icon} {s.label}: {stages[s.key]?.count ?? 0}
                      {formatMoney(stages[s.key]?.quotedTotal)
                        ? ` · ${formatMoney(stages[s.key].quotedTotal)}`
                        : ""}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CrmDashboard;
